export function motoresToNormalized(motores, cols = 6) {
    const byNumero = new Map(motores.map(m => [m.numero_motor, m]));
    const vistos = new Set();
    const slots = [];
    let cursor = 0;

    const ordenados = [...motores].sort((a, b) => a.numero_motor - b.numero_motor);

    const parceiroDe = (m) => {
        if (m.agrupado_por != null && m.agrupado_por !== 0) {
            return byNumero.get(m.agrupado_por) ?? null;
        }
        return motores.find(x => x.agrupado_por === m.numero_motor) ?? null;
    };

    ordenados.forEach((m) => {
        if (vistos.has(m.numero_motor)) return;

        const parceiro = parceiroDe(m);

        if (parceiro && !vistos.has(parceiro.numero_motor)) {
            vistos.add(m.numero_motor);
            vistos.add(parceiro.numero_motor);

            const comProduto = m.id_produto != null ? m : (parceiro.id_produto != null ? parceiro : m);
            const menor = Math.min(m.numero_motor, parceiro.numero_motor);
            const maior = Math.max(m.numero_motor, parceiro.numero_motor);

            const w = 2;
            if ((cursor % cols) + w > cols) {
                cursor = (Math.floor(cursor / cols) + 1) * cols;
            }

            slots.push({
                numero: menor,
                numeroPar: maior,
                idProduto: comProduto.id_produto ?? null,
                qtd: comProduto.qtd_produto ?? 0,
                status: comProduto.status ?? 'livre',
                agrupado: true,
                gridRow: Math.floor(cursor / cols) + 1,
                gridCol: (cursor % cols) + 1,
                colSpan: w,
                rowSpan: 1,
            });
            cursor += w;
        } else {
            vistos.add(m.numero_motor);
            slots.push({
                numero: m.numero_motor,
                idProduto: m.id_produto ?? null,
                qtd: m.qtd_produto ?? 0,
                status: m.status ?? 'livre',
                agrupado: false,
                gridRow: Math.floor(cursor / cols) + 1,
                gridCol: (cursor % cols) + 1,
                colSpan: 1,
                rowSpan: 1,
            });
            cursor += 1;
        }
    });

    return { tipo: 'maquina', cols, slots };
}

export function portasToNormalized(portas, colsHint) {
    if (!Array.isArray(portas) || portas.length === 0) {
        return { tipo: 'armario', cols: colsHint ?? 1, slots: [] };
    }

    const cols = colsHint ?? Math.max(
        1,
        ...portas.map(p => (p.coluna_inicial ?? 0) + (p.numero_colunas ?? 1))
    );

    const slots = portas.map(p => {
        const ehAgrupado = (p.numero_linhas ?? 1) > 1 || (p.numero_colunas ?? 1) > 1;
        const idProduto = p.configuracoes?.[0]?.id_produto ?? p.id_produto ?? null;

        return {
            numero: p.numero_logico,
            idProduto,
            qtd: p.configuracoes?.[0]?.qtd_produto ?? p.qtd_produto ?? 0,
            status: ehAgrupado ? 'agrupado' : (p.status ?? 'livre'),
            agrupado: ehAgrupado,
            gridRow: (p.linha_inicial ?? 0) + 1,
            gridCol: (p.coluna_inicial ?? 0) + 1,
            rowSpan: p.numero_linhas ?? 1,
            colSpan: p.numero_colunas ?? 1,
        };
    });

    return { tipo: 'armario', cols, slots };
}

export function snapshotToNormalized(snapshot, tipo, colsHint) {
    if (!Array.isArray(snapshot) || snapshot.length === 0) {
        return { tipo, cols: colsHint ?? 6, slots: [] };
    }

    if (tipo === 'armario') {
        const cols = colsHint ?? Math.max(
            ...snapshot.map(p => (p.coluna_inicial ?? 0) + (p.numero_colunas ?? 1))
        );

        const slots = snapshot.map(p => ({
            numero: p.numero_logico,
            idProduto: p.configuracoes?.[0]?.id_produto ?? null,
            codExterno: p.configuracoes?.[0]?.cod_externo ?? null,
            nomeProduto: p.configuracoes?.[0]?.nome_produto ?? null,
            qtd: 0,
            status: (p.numero_linhas > 1 || p.numero_colunas > 1) ? 'agrupado' : 'livre',
            agrupado: (p.numero_linhas > 1 || p.numero_colunas > 1),
            gridRow: (p.linha_inicial ?? 0) + 1,
            gridCol: (p.coluna_inicial ?? 0) + 1,
            rowSpan: p.numero_linhas ?? 1,
            colSpan: p.numero_colunas ?? 1,
        }));

        return { tipo, cols, slots };
    }

    const cols = colsHint ?? 6;
    return motoresToNormalized(
        snapshot.map(c => ({
            numero_motor: c.numero_motor,
            agrupado_por: c.agrupado_por,
            id_produto: c.id_produto,
            codExterno: c.cod_externo ?? null,
            nomeProduto: c.nome_produto ?? null,
            status: c.status ?? 'livre',
            qtd_produto: 0,
        })),
        cols
    );
}

export function motoresToReceiptRows(motores) {
    const rows = [];
    const consumed = new Set();
    const byNumero = new Map(motores.map(m => [m.numero_motor, m]));

    const parceiroDe = (m) => {
        if (m.agrupado_por != null && m.agrupado_por !== 0) {
            return byNumero.get(m.agrupado_por) ?? null;
        }
        return motores.find(x => x.agrupado_por === m.numero_motor) ?? null;
    };

    motores.forEach((motor) => {
        if (consumed.has(motor.numero_motor)) return;

        const parceiro = parceiroDe(motor);

        if (parceiro && !consumed.has(parceiro.numero_motor)) {
            consumed.add(motor.numero_motor);
            consumed.add(parceiro.numero_motor);

            const comProduto = motor.id_produto != null
                ? motor
                : (parceiro.id_produto != null ? parceiro : motor);

            const menor = Math.min(motor.numero_motor, parceiro.numero_motor);
            const maior = Math.max(motor.numero_motor, parceiro.numero_motor);

            rows.push({
                numero: menor,
                label: `${menor} + ${maior}`,
                id_produto: comProduto.id_produto ?? null,
                quantidade: comProduto.qtd_produto ?? 0,
                status: comProduto.status ?? 'livre',
                agrupado: true,
            });
            return;
        }

        consumed.add(motor.numero_motor);
        rows.push({
            numero: motor.numero_motor,
            label: String(motor.numero_motor),
            id_produto: motor.id_produto ?? null,
            quantidade: motor.qtd_produto ?? 0,
            status: motor.status ?? 'livre',
            agrupado: false,
        });
    });

    return rows.sort((a, b) => a.numero - b.numero);
}

const parseFisicas = (p) => {
    const raw = p.portasFisicas ?? p.portas_fisicas ?? [];
    if (typeof raw === 'string') {
        try { return JSON.parse(raw || '[]'); }
        catch { return []; }
    }
    return Array.isArray(raw) ? raw : [];
};

const isAgrupada = (p) => {
    if (typeof p.isAgrupado === 'boolean') return p.isAgrupado;
    const fisicas = parseFisicas(p);
    return fisicas.length > 1
        || (p.numeroLinhas ?? p.numero_linhas ?? 1) > 1
        || (p.numeroColunas ?? p.numero_colunas ?? 1) > 1;
};

export function portasToReceiptRows(portas) {
    const getNumero = (p) => p.numeroLogico ?? p.numero_logico ?? 0;

    const getConfigRelevante = (p) => {
        const cfgs = p.configuracoes ?? p.configuracoesPorta ?? [];
        return cfgs.find(c => (c?.produto?.id ?? c?.id_produto ?? c?.idProduto) != null)
            ?? cfgs[0] ?? null;
    };

    const rows = portas.map((porta) => {
        const cfg = getConfigRelevante(porta);
        const agrupada = isAgrupada(porta);

        return {
            numero: getNumero(porta),
            label: String(getNumero(porta) || '-'),
            id_produto:
                cfg?.produto?.id ?? cfg?.id_produto ?? cfg?.idProduto ??
                porta.id_produto ?? porta.idProduto ?? null,
            nome_produto: cfg?.produto?.nome ?? null,
            quantidade:
                cfg?.quantidade ?? cfg?.qtd_produto ?? cfg?.qtdProduto ??
                porta.quantidade ?? 0,
            status: cfg?.status ?? porta.status ?? 'livre',
            agrupado: agrupada,
            grupo: agrupada ? parseFisicas(porta).join(', ') : null,
        };
    });

    return rows.sort((a, b) => a.numero - b.numero);
}